import React from 'react';
import { cn } from '@/lib/utils';
import { Container } from '@/components/layout/Container';

export interface PageWrapperProps {
  children: React.ReactNode;
  className?: string;
  /** Container size for main content */
  containerSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /** Whether to add container padding to main content */
  containerPadding?: boolean;
  /** Custom container for main content - bypasses default Container wrapper */
  customContainer?: boolean;
  /** Layout variant */
  variant?: 'default' | 'fullwidth';
  /**
   * Header element to render outside the Container (full viewport width).
   * Use this for PageHeader with gradient backgrounds to avoid clipping.
   */
  header?: React.ReactNode;
}

/**
 * PageWrapper component for consistent page content styling
 * Used within the AppLayout to wrap page content
 * Provides consistent spacing without duplicating the layout components
 *
 * When using a PageHeader with `variant="gradient"`, pass it via the `header` prop
 * to ensure the gradient extends to the full viewport width.
 */
export const PageWrapper: React.FC<PageWrapperProps> = ({
  children,
  className,
  containerSize = 'xl',
  containerPadding = true,
  customContainer = false,
  variant: _variant = 'default',
  header,
}) => {
  // If custom container is used, don't wrap in Container
  if (customContainer) {
    return (
      <div className={cn('min-h-full', className)}>
        {header}
        <div className="overflow-x-hidden">{children}</div>
      </div>
    );
  }

  // Default behavior: wrap main content in Container with padding
  // Header renders full-width outside the Container
  return (
    <div className={cn('min-h-full', className)}>
      {header}
      <div className="overflow-x-hidden">
        <Container size={containerSize} padding={containerPadding} className="h-full">
          {children}
        </Container>
      </div>
    </div>
  );
};
